import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { interval, merge, startWith, Subject, switchMap, takeUntil, takeWhile, tap } from 'rxjs';
import { RestService } from '../../../core/services/rest.service';
import { StatusBadgeComponent } from '../../common/status-badge/status-badge.component';

export interface ExecutionCycleStatusData {
  id: string;
  name: string;
  release?: any;
  testCaseCount?: number;
  status: string;
  startTime?: string;
  endTime?: string;
  executionCycleId?: string;
  totalCount?: number;
}

const POLL_INTERVAL_MS = 5000; // 5-second refresh interval
const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED', 'ABORTED', 'EXITED', 'STOPPED', 'PASS', 'PASSED']);

@Component({
  selector: 'app-execution-cycle-status-card',
  standalone: true,
  imports: [CommonModule, StatusBadgeComponent],
  templateUrl: './execution-cycle-status-card.component.html',
  styleUrl: './execution-cycle-status-card.component.scss'
})
export class ExecutionCycleStatusCardComponent implements OnInit, OnDestroy {
  @Input() projectId!: string;
  @Input() automationProjectId!: string;
  @Input() executionId!: string;
  @Output() viewResults = new EventEmitter<string>();

  private readonly restService = inject(RestService);
  private readonly destroy$ = new Subject<void>();  // fires on component destroy
  private readonly stopPoll$ = new Subject<void>(); // fires to cancel & restart poll (retry)

  data: ExecutionCycleStatusData | null = null;
  isLoading = false;
  hasError = false;

  ngOnInit(): void {
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startPolling(): void {
    if (!this.projectId || !this.automationProjectId || !this.executionId) return;
    // Show spinner only on first load (no existing data yet)
    this.isLoading = this.data === null;
    this.hasError = false;

    interval(POLL_INTERVAL_MS).pipe(
      startWith(0), // trigger immediately without waiting first interval
      switchMap(() =>
        this.restService.getAutomationExecution(this.projectId, this.automationProjectId, this.executionId)
      ),
      tap((res: any) => {
        this.data = res?.data ?? res;
        this.isLoading = false;
        this.hasError = false;
      }),
      // stop automatically once a terminal status is reached (inclusive — applies the last value first)
      takeWhile(() => !this.isTerminal(), true),
      // stop when component is destroyed OR a manual retry is triggered
      takeUntil(merge(this.destroy$, this.stopPoll$))
    ).subscribe({
      error: () => {
        this.hasError = true;
        this.isLoading = false;
      }
    });
  }

  private isTerminal(): boolean {
    return !!this.data?.status && TERMINAL_STATUSES.has(this.data.status.toUpperCase());
  }

  /** Called by the Retry button — cancels the current poll and restarts fresh */
  fetchData(): void {
    this.stopPoll$.next();
    this.data = null;
    this.startPolling();
  }

  onViewResults(): void {
    this.viewResults.emit(this.data?.executionCycleId ?? this.data?.id ?? this.executionId);
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);

    const hours24 = date.getHours();
    const hours12 = hours24 % 12 || 12;
    const hour = String(hours12).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    const second = String(date.getSeconds()).padStart(2, '0');
    const meridiem = hours24 >= 12 ? 'pm' : 'am';

    return `${day}/${month}/${year}, ${hour}:${minute}.${second} ${meridiem}`;
  }

  calculateDuration(): string {
    if (!this.data?.startTime || !this.data?.endTime) return 'N/A';
    // Truncate to whole seconds so the duration matches the displayed HH:MM:SS values
    const start = Math.floor(new Date(this.data.startTime).getTime() / 1000) * 1000;
    const end = Math.floor(new Date(this.data.endTime).getTime() / 1000) * 1000;
    const diff = end - start;
    if (diff < 0) return 'N/A';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  }
}

